import { useState } from "react";
import type { CSSProperties, FormEvent, ReactElement } from "react";

import { Icon, StatusPill } from "../design-system";
import { ApiError, type Credentials, type SessionState } from "../api/client";
import { Aborted } from "../session/passkey";
import { useSkin } from "../shell/skin";
import wordmarkDark from "../design/assets/logo/silvererp-logo-negativ.svg";
import wordmarkLight from "../design/assets/logo/silvererp-logo-positiv.svg";

/**
 * Das Anmeldebild. Angemeldet wird mit den Zugangsdaten des SilverERP — es gibt
 * keine eigene Benutzerverwaltung und kein zweites Passwort.
 *
 * Es gehört zum **Rahmen** (ADR-0025) und wird nachgezeichnet, nicht komponiert:
 * Vorlage ist `design/reference/LoginScreen.jsx`, beschrieben in `FRAME.md`
 * unter „Das Anmeldebild". Es rendert **außerhalb** der Hülle — kein Band, keine
 * Leiste, keine Wortmarke in einer Kopfzeile —, und zwar in sechs Schichten von
 * hinten nach vorn: Grund, Aura A, Aura B, Vignette, Wortmarke, Karte. Jeder
 * Wert kommt aus `design/tokens/frame.css`; erfunden wird keiner.
 *
 * Zwei Feinheiten, die das Bild ausmachen und beim Nachbauen gern verloren gehen:
 * Die Auren sind das **Login-Paar** und nicht die ambienten Wolken der Anwendung
 * — mit jenen rendert der Schirm als flaches Schwarz —, und die Karte ist **per
 * Definition durchscheinend**: `--blur-glass` über einer deckenden Fläche tut
 * nichts.
 *
 * Die Felder und der Anmeldeknopf stehen als rohes `<input>` und `<button>` da
 * und nicht als Bausteine des Baukastens. Das ist die Vorlage: Der Login teilt
 * die Maße der Maske nicht — seine Felder sind 38 statt 26 hoch, ihre Fläche ist
 * `--bg-login-field` und ihr Radius `--r-field-login`.
 *
 * Sie spricht nie mit dem SilverERP. Sie ruft die eigene API, und die hält die
 * Sitzung serverseitig.
 *
 * Zwei Absagen, die auseinandergehalten werden müssen: Ein falsches Passwort ist
 * eine Absage der Gegenstelle, ein Ausfall der Gegenstelle ist keine — wer bei
 * stehendem SilverERP „Passwort nicht korrekt" liest, tippt sein Passwort noch
 * dreimal und sucht dann den Fehler bei sich.
 *
 * **Der zweite Weg in dieselbe Sitzung** ist die biometrische Anmeldung (#159).
 * Sie steht als Knopf unter dem Primärbutton — und **fehlt ganz**, wo auf diesem
 * Gerät kein Zugang eingerichtet ist. Entschieden wird das, bevor dieses Bild
 * überhaupt gezeichnet wird: Ein Knopf, der nachgereicht wird, sobald eine
 * asynchrone Prüfung zurückkommt, springt in eine Karte hinein, die schon steht.
 *
 * `unavailable` ist die Meldung eines **stehenden** Ausfalls, sonst leer: Seit das
 * Bild außerhalb der Hülle steht, gibt es keine Kopfleiste mehr, die ihn nebenbei
 * nennen könnte. Er steht deshalb hier — und rüttelt nicht, denn er ist keine
 * Antwort auf einen Versuch.
 */
export function SignInView({
  signIn,
  onSignedIn,
  expired = false,
  unavailable = "",
  passkey = null,
}: {
  signIn: (credentials: Credentials) => Promise<SessionState>;
  onSignedIn: (state: SessionState) => void;
  expired?: boolean;
  unavailable?: string;
  /**
   * Die biometrische Anmeldung — oder `null`, und dann **fehlt** der Knopf.
   *
   * Kein `disabled` und kein Hinweis: Ein Biometrieknopf auf einem Rechner ohne
   * eingerichteten Zugang ist ein Versprechen, hinter dem nichts steht, und
   * ausgegraut ist keine mildere Fassung davon, sondern dieselbe Zusage in
   * blass. Wer entscheidet, dass es ihn gibt, entscheidet das **vor** dem ersten
   * Zeichnen — hier steht nur noch, ob er da ist.
   */
  passkey?: (() => Promise<SessionState>) | null;
}): ReactElement {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reveal, setReveal] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);
  const { skin } = useSkin();
  // Die Zahl der Absagen. Sie hängt als Schlüssel an der Karte: Ein Stil, der sich
  // nicht ändert, startet keine Animation neu — zweimal dasselbe falsche Passwort
  // rüttelte sonst nur beim ersten Mal.
  const [refusals, setRefusals] = useState(0);

  const refuse = (reason: ApiError): void => {
    setError(reason);
    setRefusals((count) => count + 1);
  };

  const submit = (event: FormEvent): void => {
    event.preventDefault();

    if (busy) {
      return;
    }

    if (username.trim() === "" || password === "") {
      refuse(new ApiError("Bitte Benutzername und Passwort eingeben."));

      return;
    }

    setBusy(true);
    setError(null);

    signIn({ username: username.trim(), password }).then(
      (state) => {
        setBusy(false);
        onSignedIn(state);
      },
      (reason: unknown) => {
        setBusy(false);
        refuse(
          reason instanceof ApiError
            ? reason
            : new ApiError(reason instanceof Error ? reason.message : String(reason)),
        );
      },
    );
  };

  const clear = (): void => {
    setError(null);
  };

  const startPasskey = (): void => {
    if (busy || passkey === null) {
      return;
    }

    setBusy(true);
    setError(null);

    passkey().then(
      (state) => {
        setBusy(false);
        onSignedIn(state);
      },
      (reason: unknown) => {
        setBusy(false);

        // Ein Abbruch am Gerätedialog wird nicht rot gezeigt und gar nicht
        // gezeigt: Wer den Dialog wegtippt, hat sich entschieden.
        if (reason instanceof Aborted) {
          return;
        }

        // Abgelaufen, widerrufen, andere Adresse: Der Fehlschlag steht im
        // Fehlerband wie jeder andere, und der Weg zurück ist das Passwort. Ein
        // eigener Bildschirm dafür wäre eine Sackgasse mit Aussicht.
        refuse(
          reason instanceof ApiError
            ? reason
            : new ApiError(reason instanceof Error ? reason.message : String(reason)),
        );
      },
    );
  };

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "var(--sp-10)",
        background: "var(--bg-login)",
        color: "var(--fg-2)",
        fontFamily: "var(--font-sans)",
      }}
    >
      {/* Schicht 2 und 3: die beiden atmenden Auren. Sie sind das Bild. */}
      <div
        style={{
          position: "absolute",
          inset: "-25%",
          pointerEvents: "none",
          background:
            "radial-gradient(38% 40% at 50% 50%, var(--aura-1-login) 0%, var(--aura-1-off) 70%)",
          filter: "var(--blur-aura-1-login)",
          animation: "erpAuraA var(--dur-aura-1) ease-in-out infinite",
        }}
      />
      <div
        style={{
          position: "absolute",
          inset: "-25%",
          pointerEvents: "none",
          background:
            "radial-gradient(30% 32% at 50% 50%, var(--aura-2-login) 0%, var(--aura-2-off) 68%)",
          filter: "var(--blur-aura-2-login)",
          animation: "erpAuraB var(--dur-aura-2) ease-in-out infinite",
        }}
      />
      {/* Schicht 4: die Vignette. Sie ist der Grund, warum die Aura den
          Fensterrand nicht berührt. */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          background: "var(--login-vignette)",
        }}
      />

      <div
        style={{
          position: "relative",
          zIndex: 2,
          width: "var(--w-login-card)",
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Schicht 5: die Wortmarke über der Karte, nicht in einer Leiste. Auf
            der hellen Skin das positive Signet, auf der dunklen das negative —
            beide zweifarbig und unverändert als `<img>` gezeigt, nie eingefärbt. */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            paddingBottom: "var(--sp-login-mark)",
          }}
        >
          <img
            src={skin === "light" ? wordmarkLight : wordmarkDark}
            width={176}
            alt="SilverERP"
          />
        </div>

        {/* Schicht 6: die Karte. Durchscheinend, und sie rüttelt mit dem Band. */}
        <div
          key={refusals}
          style={{
            background: "var(--bg-login-card)",
            backdropFilter: "var(--blur-glass)",
            WebkitBackdropFilter: "var(--blur-glass)",
            border: "1px solid var(--line-c)",
            borderRadius: "var(--r-surface)",
            boxShadow: "var(--shadow-login)",
            animation: error === null ? "none" : "erpShake var(--dur-shake) ease",
          }}
        >
          <div style={{ padding: "var(--pad-login-head)" }}>
            <h1
              style={{
                margin: 0,
                fontSize: "var(--fs-19)",
                fontWeight: "var(--fw-semibold)",
                letterSpacing: "var(--ls-title)",
                color: "var(--fg-0)",
              }}
            >
              Anmelden
            </h1>
            <p
              style={{
                margin: 0,
                marginTop: 7,
                fontSize: "var(--fs-13)",
                lineHeight: "var(--lh-normal)",
                color: "var(--fg-7)",
                textWrap: "pretty",
              }}
            >
              Melden Sie sich mit Ihrem SilverERP-Konto an.
            </p>
          </div>

          <form
            onSubmit={submit}
            style={{ display: "flex", flexDirection: "column", padding: "var(--pad-login-form)" }}
          >
            <label htmlFor="sign-in-username" style={labelStyle}>
              Benutzername
            </label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={glyphStyle}>
                <Icon name="user" size={15} />
              </span>
              <input
                id="sign-in-username"
                type="text"
                value={username}
                onChange={(event) => {
                  setUsername(event.target.value);
                  clear();
                }}
                autoComplete="username"
                placeholder="Benutzername im SilverERP"
                style={fieldStyle(error !== null)}
              />
            </div>

            <label
              htmlFor="sign-in-password"
              style={{ ...labelStyle, paddingTop: "var(--sp-8)" }}
            >
              Passwort
            </label>
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <span style={glyphStyle}>
                <Icon name="lock" size={15} />
              </span>
              <input
                id="sign-in-password"
                type={reveal ? "text" : "password"}
                value={password}
                onChange={(event) => {
                  setPassword(event.target.value);
                  clear();
                }}
                autoComplete="current-password"
                placeholder="Passwort eingeben"
                style={fieldStyle(error !== null)}
              />
              <button
                type="button"
                onClick={() => setReveal(!reveal)}
                title={reveal ? "Passwort verbergen" : "Passwort anzeigen"}
                style={{
                  position: "absolute",
                  right: 6,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 26,
                  height: 26,
                  background: "transparent",
                  border: "none",
                  borderRadius: "var(--r-field)",
                  color: "var(--ico-b)",
                  cursor: "pointer",
                }}
              >
                <Icon name={reveal ? "eye-off" : "eye"} size={15} />
              </button>
            </div>

            {error !== null ? (
              <Failure
                message={error.message}
                notConnected={error.code === "not_connected"}
                shaking
              />
            ) : null}
            {error === null && unavailable !== "" ? (
              <Failure message={unavailable} notConnected shaking={false} />
            ) : null}
            {error === null && unavailable === "" && expired ? (
              <Notice
                text="Ihre Sitzung ist abgelaufen. Bitte melden Sie sich erneut an."
                glyph="timer"
              />
            ) : null}

            <div style={{ height: "var(--sp-9)" }} />

            <button
              type="submit"
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 9,
                height: "var(--h-field-login)",
                background: busy ? "var(--ac-brand-2)" : "var(--ac-brand)",
                border: "1px solid var(--ac-brand-line)",
                borderRadius: "var(--r-field-login)",
                color: "var(--fg-on-brand)",
                fontFamily: "inherit",
                fontSize: "var(--fs-13)",
                fontWeight: "var(--fw-semibold)",
                cursor: "pointer",
              }}
            >
              {busy ? (
                <span style={{ display: "flex", animation: "erpSpin var(--dur-spin) linear infinite" }}>
                  <Icon name="loader-2" size={15} />
                </span>
              ) : null}
              {busy ? "Anmeldung läuft ..." : "Anmelden"}
            </button>

            {/* Der Knopf für die biometrische Anmeldung: nach dem Primärbutton
                und vor dem Trenner „oder fortfahren mit" — er ist kein
                Fremdanbieter, sondern ein zweiter Weg zu demselben Konto. Den
                Trenner und die vier Anbieter hat dieses Produkt nicht; er steht
                deshalb als letztes in der Karte.

                **Stille Behandlung**, wie die Anbieterknöpfe darunter es wären:
                Die laute gehört dem Anmelden mit Passwort. `--ac-brand` trägt in
                dieser Karte genau ein Knopf, sonst hat sie zwei Hauptwege und
                damit keinen. */}
            {passkey === null ? null : (
              <button
                type="button"
                onClick={startPasskey}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                  marginTop: "var(--sp-6)",
                  height: "var(--h-login-provider)",
                  background: "var(--bg-login-field)",
                  border: "1px solid var(--line-d)",
                  borderRadius: "var(--r-field-login)",
                  color: "var(--fg-3)",
                  fontFamily: "inherit",
                  fontSize: "var(--fs-12)",
                  fontWeight: "var(--fw-medium)",
                  cursor: "pointer",
                }}
              >
                <Icon name="fingerprint" size={14} />
                Mit Biometrie anmelden
              </button>
            )}
          </form>
        </div>

        {/* Die Fußzeile unter der Karte, nicht in ihr (FRAME.md, „Das Anmeldebild"). */}
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            paddingTop: "var(--sp-9)",
          }}
        >
          <span style={{ fontSize: "var(--fs-11)", color: "var(--fg-9)" }}>
            © SilverERP. Alle Rechte vorbehalten.
          </span>
        </div>
      </div>
    </div>
  );
}

/** Die Beschriftung eines Feldes. */
const labelStyle: CSSProperties = {
  fontSize: "var(--fs-12)",
  fontWeight: "var(--fw-medium)",
  color: "var(--fg-4)",
  paddingBottom: 7,
};

/** Die Glyphe am linken Rand eines Feldes. */
const glyphStyle: CSSProperties = {
  position: "absolute",
  left: 10,
  display: "flex",
  color: "var(--ico-c)",
  pointerEvents: "none",
};

/**
 * Ein Feld des Anmeldebilds. Es ist nicht das Feld der Maske: höher, mit eigener
 * Fläche und eigenem Radius. Der linke und der rechte Innenabstand halten Glyphe
 * und Augensymbol frei; für beide Maße führt das System kein Token.
 */
function fieldStyle(invalid: boolean): CSSProperties {
  return {
    width: "100%",
    height: "var(--h-field-login)",
    background: "var(--bg-login-field)",
    border: "1px solid " + (invalid ? "var(--login-err-field)" : "var(--line-d)"),
    borderRadius: "var(--r-field-login)",
    outline: "none",
    paddingLeft: 34,
    paddingRight: 34,
    color: "var(--fg-1)",
    fontFamily: "inherit",
    fontSize: "var(--fs-13)",
  };
}

/**
 * Das Fehlerband. Sein Rot ist ein eigener Ton und ausdrücklich **nicht**
 * `--ac-red`: Das Statusrot der Anwendung säße daneben und läse sich nicht wie es.
 *
 * „nicht verbunden" steht als eigene Marke darin, wenn die Gegenstelle steht —
 * das ist ein anderer Zustand als ein falsches Passwort, und er ist nicht durch
 * nochmaliges Tippen zu beheben. Die Glyphe folgt dieser Unterscheidung: ein
 * gezogener Stecker für den Ausfall, sonst der Kreis mit dem Ausrufezeichen
 * (`circle-alert` ist der heutige Lucide-Name des `alert-circle` aus `FRAME.md`).
 */
function Failure({
  message,
  notConnected,
  shaking,
}: {
  message: string;
  notConnected: boolean;
  shaking: boolean;
}): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: "var(--sp-4)",
        marginTop: "var(--sp-7)",
        paddingTop: 9,
        paddingRight: 11,
        paddingBottom: 9,
        paddingLeft: 11,
        background: "var(--login-err-bg)",
        border: "1px solid var(--login-err-line)",
        borderRadius: "var(--r-tile)",
        color: "var(--login-err-fg)",
        fontSize: "var(--fs-12)",
        lineHeight: "var(--lh-normal)",
        animation: shaking ? "erpShake var(--dur-shake) ease" : "none",
      }}
    >
      <span style={{ display: "flex", flex: "none", marginTop: 1 }}>
        <Icon name={notConnected ? "unplug" : "circle-alert"} size={14} />
      </span>
      <span style={{ flex: 1, textWrap: "pretty" }}>
        {notConnected ? <StatusPill tone="red">nicht verbunden</StatusPill> : null}
        {notConnected ? " " : null}
        {message}
      </span>
    </div>
  );
}

/** Ein Hinweis ohne Absage — die abgelaufene Sitzung. Kein Fehlerband: Es ist keiner. */
function Notice({ text, glyph }: { text: string; glyph: string }): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-4)",
        marginTop: "var(--sp-7)",
        fontSize: "var(--fs-12)",
        color: "var(--fg-7)",
        textWrap: "pretty",
      }}
    >
      <Icon name={glyph} size={14} />
      {text}
    </div>
  );
}
